/**
 * Core TypeScript Interfaces for appaihelp.com
 * Multi-service AI Platform Data Models
 */

// ============================================
// User & Authentication
// ============================================

// ✨ NEW: User Quota Info
export interface UserQuota {
  percentageUsed: number;
  packageStatus: 'available' | 'low' | 'empty';
  hasAdvanceBalance: boolean;
  advancePercentageUsed: number;
  /** Package advance allowance + purchased advance balance, as one figure. */
  advanceTotalPercentageUsed: number;
  advanceTotalStatus: 'available' | 'low' | 'empty';
  canUseAdvanceFeatures: boolean;
  advanceFallbackEnabled: boolean;
  canSend: boolean;
  nextResetAt: string;
  cycleStartAt: string;
}

export type TokenUsagePeriod = 'day' | 'month' | 'year';

export interface TokenUsagePoint {
  period: string;
  label: string;
  totalTokens: number;
  /** Credits charged in the bucket (the unit quota is spent in). */
  totalCredits: number;
  promptTokens: number;
  completionTokens: number;
  requestCount: number;
}

export interface TokenUsageSeries {
  period: TokenUsagePeriod;
  points: TokenUsagePoint[];
  totalTokens: number;
  totalCredits: number;
  requestCount: number;
}

export type ThirdPartyUsageGroupBy =
  | 'agent'
  | 'external_tenant'
  | 'day'
  | 'api_key'
  | 'capability'
  | 'tool';

export interface ThirdPartyUsageRow {
  client_id: string | null;
  external_tenant_type: string | null;
  external_tenant_id: string | null;
  agent_public_id: string | null;
  api_key_id: number | null;
  capability: string | null;
  day: string | null;
  tool_id?: string | null;
  messages: number;
  total_tokens: number;
  cost_credits: number;
  cost_usd?: number;
  tool_calls: number;
  successful_tool_calls?: number;
  failed_tool_calls?: number;
  avg_execution_ms?: number | null;
}

export interface ThirdPartyUsageResponse {
  data: ThirdPartyUsageRow[];
}

export type ApiKeyUsageGroupBy = 'agent' | 'day' | 'api_key' | 'capability';

export interface ApiKeyUsageRow {
  api_key_id: number | null;
  api_key_public_id: string | null;
  agent_public_id: string | null;
  capability: string | null;
  day: string | null;
  external_tenant_id: string | null;
  external_tenant_type: string | null;
  client_id: string | null;
  requests: number;
  usage_units: number;
  charged_credits: number;
  latest_request_id: string | null;
  latest_used_at: string | null;
}

export interface ApiKeyUsageResponse {
  data: ApiKeyUsageRow[];
}

// ✨ NEW: Subscription Info (Enhanced)
export interface SubscriptionInfo {
  status: 'trialing' | 'active' | 'expired' | 'canceled';
  packageName: string;
  packageCode: string;
  isTrialing: boolean;
  trialDaysRemaining: number | null;
  expiresAt: string;
}

export interface User {
  publicId: string; // Backend uses publicId instead of id
  userName: string; // Backend uses userName instead of name
  fullName?: string;
  email: string;
  role: string; // ADMIN, USER, etc.
  authProvider?: string;
  avatar?: string;
  avatarType?: 'image' | 'video';

  // ✨ NEW: Enhanced quota & subscription
  quota?: UserQuota;
  subscription?: SubscriptionInfo;
  setting?: UserSettings;
  allowDynamicTheme?: boolean;

  createdAt?: Date;
  updatedAt?: Date;
}

export type BackgroundThemeId = 'none' | 'matrix' | 'petals' | 'comets' | 'stars' | 'snow';

export interface UserSettings {
  id?: number;
  publicId?: string;
  useAdvanceTokensForChatFallback: boolean;
  emailNotifications?: boolean;
  preferredLanguage?: string;
  backgroundTheme?: BackgroundThemeId;
  backgroundThemeIntensity?: number;
  extraSettingsJson?: string | null;
}

export type SubscriptionTier = 'free' | 'pro' | 'enterprise';

// ============================================
// AI Agent Configuration
// ============================================

export interface Agent {
  id: string;
  publicId: string; // Backend agent configuration identifier
  name: string;
  description?: string;
  avatarUrl?: string | null;
  avatarType?: 'image' | 'video';
  characteristicIds: string[];
  knowledgeIds: string[];
  ownerType?: 'SYSTEM' | 'USER' | 'PROJECT' | 'INTERNAL';
  ownerId?: number;
  userId?: string;
  isActive?: boolean;
  status?: 'draft' | 'published' | 'archived' | 'disabled' | string;
  version?: number;
  publishedVersion?: number | null;
  publishedAt?: Date | string | null;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
  // Association totals returned by the list endpoint
  characteristicCount?: number;
  knowledgeCount?: number;
  // Populated relations
  characteristics?: Characteristic[];
  knowledges?: Knowledge[];
}

export interface CreateAgentInput {
  name: string;
  description?: string;
  avatarUrl?: string | null;
  characteristicIds: string[];
  knowledgeIds: string[];
  ownerType?: 'USER' | 'PROJECT';
  ownerId?: number;
}

export interface AgentWizardAnswers {
  agentName: string;
  purpose: string;
  mainTasks: string;
  targetUsers: string;
  personality: string;
  tone: string;
  languageStyle: string;
  knowledgeSummary: string;
  faq: string;
  processes: string;
  policies: string;
  constraints: string;
  handoffRules: string;
}

export interface AgentWizardDraft {
  agent: Pick<CreateAgentInput, 'name' | 'description' | 'avatarUrl'>;
  characteristics: CreateCharacteristicInput[];
  knowledges: CreateKnowledgeInput[];
  warnings: string[];
}

// Deprecated - kept for backward compatibility during migration
export interface LegacyAgent {
  id: string;
  name: string;
  role: string;
  systemPrompt: string;
  creativityLevel: number;
  userId: string;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================
// Capabilities
// ============================================

export interface Capability {
  publicId: string;
  code: string;
  name: string;
  description: string;
}

// ============================================
// Characteristics
// ============================================

export type CharacteristicScope = 'system' | 'user' | 'project' | 'all';
export type CharacteristicLayer =
  | 'identity'
  | 'tone_style'
  | 'values'
  | 'behavior'
  | 'constraints'
  | 'domain';

export interface Characteristic {
  publicId: string;
  code: string;
  name: string;
  description?: string | null;
  layer: CharacteristicLayer | string;
  prompt: string;
  sortOrder?: number;
  priority?: number;
  status?: string;
  visibility?: string;
  isSystem: boolean;
  /** Active agents using this characteristic (list endpoint, non-system only) */
  agentCount?: number;
  userId?: number;
  projectId?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateCharacteristicInput {
  code: string;
  name: string;
  description?: string;
  layer: CharacteristicLayer;
  prompt: string;
  sortOrder?: number;
  status?: 'published';
  projectId?: number;
}

export interface UpdateCharacteristicInput {
  code?: string;
  name?: string;
  description?: string | null;
  layer?: CharacteristicLayer;
  prompt?: string;
  sortOrder?: number;
  status?: 'published' | 'draft' | 'archived';
}

// ============================================
// Knowledge
// ============================================

export type KnowledgeScope = 'system' | 'user' | 'project' | 'all';
export type KnowledgeSourceType = 'text' | 'file' | 'url';
export type KnowledgeSyncStatus =
  | 'pending'
  | 'processing'
  | 'success'
  | 'partial'
  | 'failed'
  | string;

export interface KnowledgeFileInfo {
  publicId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  status: string;
}

export interface Knowledge {
  publicId: string;
  name: string;
  description?: string;
  sourceType: KnowledgeSourceType;
  source?: KnowledgeSourceType;
  sourceContent?: string;
  ownerType: 'SYSTEM' | 'USER' | 'PROJECT';
  ownerId?: number;
  createdAt: Date | string;
  updatedAt?: Date | string;
  syncStatus?: KnowledgeSyncStatus | null;
  syncedAt?: Date | string | null;
  chunkCount?: number | null;
  vectorCount?: number | null;
  errorSummary?: string | null;
  files?: KnowledgeFileInfo[];
}

export interface CreateKnowledgeInput {
  name: string;
  description?: string;
  sourceType: KnowledgeSourceType;
  content?: string;
  sourceUrl?: string;
  files?: File[];
  projectId?: number | string;
}

// ============================================
// User Memories
// ============================================

export type UserMemoryScopeType = 'USER' | 'AGENT' | 'PROJECT';
export type UserMemoryValueType = 'text' | 'json' | 'boolean' | 'number';
export type UserMemorySource = 'explicit' | 'inferred' | 'system' | 'imported';
export type UserMemoryStatus = 'ACTIVE' | 'SUPERSEDED' | 'DELETED';

export interface UserMemory {
  publicId: string;
  scopeType: UserMemoryScopeType;
  scopeId: number;
  category: string;
  key: string;
  value: string;
  originalText?: string | null;
  valueType: UserMemoryValueType | string;
  confidence: number;
  importance: number;
  source: UserMemorySource | string;
  status: UserMemoryStatus | string;
  lastUsedAt?: Date | string | null;
  lastReinforcedAt?: Date | string | null;
  expiresAt?: Date | string | null;
  useCount: number;
  feedbackScore?: number;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export type UserMemoryRating = 'like' | 'dislike';

export interface UserMemoryFeedbackResult {
  publicId: string;
  feedbackScore: number;
  confidence: number;
  status: UserMemoryStatus | string;
}

export interface CreateUserMemoryInput {
  /** Project public ID: stores the memory in that project's scope. */
  projectId?: string;
  scopeType?: UserMemoryScopeType;
  scopeId?: number;
  category: string;
  key: string;
  value: string;
  originalText?: string;
  valueType?: UserMemoryValueType;
  confidence?: number;
  importance?: number;
  source?: UserMemorySource;
  expiresAt?: string | null;
}

export type UpdateUserMemoryInput = Partial<CreateUserMemoryInput>;

// ============================================
// Translation Service
// ============================================

export interface TranslationRequest {
  sourceText: string;
  sourceLanguage: Language;
  targetLanguages: Language[];
  agentId: string;
}

export interface TranslationResult {
  id: string;
  targetLanguage: Language;
  translatedText: string;
  sourceText: string;
  sourceLanguage: Language;
  agentId: string;
  status: TranslationStatus;
  createdAt: Date;
  previewUnavailable?: boolean;
  download?: {
    downloadUrl: string;
    fileName: string;
  };
}

export type TranslationStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface Language {
  code: string; // ISO 639-1 code (e.g., 'en', 'fr', 'ja')
  aliases?: string[]; // Additional accepted translation keys mapped to this language
  name: string; // Display name (e.g., 'English', 'French', 'Japanese')
  nativeName: string; // Native name (e.g., 'English', 'Français', '日本語')
  countryCode?: string; // ISO 3166-1 alpha-2 code used for flag image URLs
  flagUrl?: string; // Uploaded flag image URL from admin language settings
}

// ============================================
// Common Language Presets
// ============================================

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', countryCode: 'gb' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', countryCode: 'es' },
  { code: 'fr', name: 'French', nativeName: 'Français', countryCode: 'fr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', countryCode: 'de' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', countryCode: 'it' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', countryCode: 'pt' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', countryCode: 'ru' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', countryCode: 'jp' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', countryCode: 'kr' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', countryCode: 'vn' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', countryCode: 'cn' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', countryCode: 'sa' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', countryCode: 'in' },
];

// ============================================
// Theme & UI State
// ============================================

export type ThemeMode = 'light' | 'dark';

export interface ThemeContextValue {
  mode: ThemeMode;
  toggleTheme: () => void;
}

// ============================================
// Navigation & Routes
// ============================================

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon?: string;
}

export interface ServiceNavItem extends NavItem {
  description?: string;
  badge?: string;
}

// ============================================
// API Key Management
// ============================================

/** Actions a scope can grant for one capability */
export interface ApiKeyScopeInput {
  capabilityCode: string;
  canExecute: boolean;
  canCreate: boolean;
  canDelete: boolean;
}

export interface ApiKeyApiScopeInput {
  scope: string;
  resourceType?: string;
  resourceId?: number;
  resourcePublicId?: string;
}

export interface ApiKeyApiScopeResponse {
  scope: string;
  name: string;
  capabilityCode: string;
  resourceType?: string | null;
  resourceId?: number | null;
  resourcePublicId?: string | null;
}

export interface ApiScopeCatalogItem {
  scope: string;
  name: string;
  description: string;
  resourceType: string;
  action: string;
  capabilityCode?: string;
  capabilityPublicId?: string;
  aliases: string[];
  resourceMode: 'none' | 'optional' | 'required';
  resourceRestrictionTypes: string[];
  source: 'static' | 'capability';
  directExecutionPath?: string;
}

/** Capability scope as returned on an API key */
export interface ApiKeyScopeResponse {
  capabilityCode: string;
  capabilityName: string;
  canExecute: boolean;
  canCreate: boolean;
  canDelete: boolean;
}

export interface ApiKey {
  publicId: string;
  prefix?: string | null;
  environment?: string | null;
  status: 'ACTIVE' | 'REVOKED';
  metadata?: {
    appName?: string;
    environment?: string;
    description?: string;
  };
  scopes?: ApiKeyApiScopeResponse[];
  capabilities: ApiKeyScopeResponse[];
  createdAt: string;
  expiresAt?: string | null;
  revokedAt?: string | null;
  lastUsed?: string | null;
  rateLimitPerMinute?: number | null;
  rateLimitPerDay?: number | null;
}

export interface CreateApiKeyInput {
  capabilities?: ApiKeyScopeInput[];
  scopes?: ApiKeyApiScopeInput[];
  metadata?: {
    appName?: string;
    environment?: string;
    description?: string;
    purpose?: string;
  };
  expiresAt?: string;
  rateLimit?: {
    requestsPerMinute?: number;
    requestsPerDay?: number;
  };
}

export interface CreateApiKeyResponse extends ApiKey {
  /** Raw secret — shown ONLY ONCE at creation time */
  apiKey: string;
}

export interface CreateIntegrationClientInput {
  name: string;
  environment?: string;
  status?: string;
  metadata?: Record<string, unknown>;
}

export interface IntegrationClient {
  public_id: string;
  name: string;
  environment: string;
  status: string;
  metadata?: Record<string, unknown> | null;
  binding_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ExternalAgentBindingUsage {
  messages: number;
  total_tokens: number;
  cost_credits: number;
  tool_calls: number;
}

export interface ExternalAgentBinding {
  public_id: string;
  client_id: string;
  agent_public_id: string;
  agent_name: string;
  external_tenant_type?: string | null;
  external_tenant_id: string;
  status: string;
  metadata?: Record<string, unknown> | null;
  usage?: ExternalAgentBindingUsage;
  created_at?: string;
  updated_at?: string;
}

export interface UpsertIntegrationClientBindingInput {
  agent_public_id: string;
  external_tenant_id: string;
  external_tenant_type?: string;
  status?: string;
  metadata?: Record<string, unknown>;
}

export interface ProvisionIntegrationClientAgentInput {
  external_tenant_id: string;
  external_tenant_type?: string;
  template_agent_public_id?: string;
  name?: string;
  description?: string;
  avatar_url?: string;
  characteristic_ids?: string[];
  knowledge_ids?: string[];
  status?: string;
  metadata?: Record<string, unknown>;
}

export interface ProvisionIntegrationClientAgentResponse {
  agent: {
    public_id: string;
    name: string;
  };
  binding: ExternalAgentBinding;
}

export interface UpdateIntegrationClientBindingInput {
  status?: string;
  metadata?: Record<string, unknown>;
}

export interface EmbedWidget {
  public_id: string;
  name: string;
  key_prefix: string | null;
  environment: string;
  status: string;
  agent: {
    public_id: string;
    name: string;
    status: string | null;
    is_active: boolean | null;
    avatar_url?: string | null;
  } | null;
  allowed_origins: string[];
  theme: {
    position: string;
    primary_color: string;
    accent_color: string;
    border_radius: string;
    size: string;
    launcher_icon_url?: string | null;
  };
  behavior: {
    greeting: string | null;
    placeholder: string;
    auto_open: boolean;
    delay_seconds: number;
    sound_enabled: boolean;
    show_branding: boolean;
  };
  limits: {
    max_messages_per_session: number;
    max_sessions_per_day: number;
  };
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface CreateEmbedWidgetInput {
  name?: string;
  agent_public_id: string;
  allowed_origins: string[];
  widget_position?: string;
  primary_color?: string;
  accent_color?: string;
  launcher_icon_url?: string | null;
  custom_welcome_message?: string;
  custom_placeholder?: string;
  auto_open?: boolean;
  sound_enabled?: boolean;
  max_messages_per_session?: number;
  max_sessions_per_day?: number;
}

export interface CreateEmbedWidgetResponse {
  widget: EmbedWidget;
  widget_key: string;
  embed_snippet: string;
}

export * from './subscription';

// ============================================
// Subscription & Billing
// ============================================

// ============================================
// Chat & Messaging
// ============================================

export interface ChatSession {
  id: string;
  publicId?: string;
  internalId?: number;
  agentId: string | number;
  agentPublicId: string;
  agentName: string;
  agentKey?: string;
  title: string; // Auto-generated or custom title
  lastMessage?: string; // Preview of last message
  lastMessageAt: Date;
  createdAt: Date;
  updatedAt: Date;
  status?: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  messageCount?: number;
  tokenCount?: number;
  entrypoint?: string | null;
  sourceType?: 'web' | 'channel' | 'api_key' | 'embed' | 'webhook' | 'other' | string;
  sourceProvider?: 'appaihelp' | 'telegram' | 'discord' | 'api_key' | 'embed_widget' | string;
  sourcePublicId?: string | null;
  sourceDisplayName?: string | null;
  externalTenantType?: string | null;
  externalTenantId?: string | null;
  externalUserId?: string | null;
  externalSessionId?: string | null;
  channelConversationPublicId?: string | null;
  channelChatType?: string | null;
  channelChatTitle?: string | null;
  channelChatUsername?: string | null;
  /** Set when the conversation belongs to a user-created project. */
  projectPublicId?: string | null;
  projectName?: string | null;
  limitWarning?: {
    level: 'near_limit' | 'limit_reached' | string;
    message: string;
  } | null;
}

export interface ChannelIntegration {
  public_id: string;
  provider: 'telegram' | 'discord' | string;
  name: string;
  status: 'active' | 'disabled' | 'disabled_by_package' | 'revoked' | 'error' | string;
  bot_username?: string | null;
  bot_token_prefix?: string | null;
  webhook_url?: string | null;
  webhook_status?: string;
  last_error?: string | null;
  settings?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  agent_public_id?: string | null;
  agent_name?: string | null;
  conversation_count?: number;
  created_at: string;
  updated_at: string;
  disabled_at?: string | null;
  last_webhook_at?: string | null;
}

export type EmailProvider = 'gmail' | 'outlook';

export interface EmailAgentBinding {
  public_id: string;
  agent_public_id?: string | null;
  agent_name?: string | null;
  agent_status?: string | null;
  agent_active?: boolean;
  access_level: 'read' | 'summarize' | 'draft_action' | 'execute_action' | string;
  summary_mode: 'off' | 'on_demand' | 'proactive_digest' | 'immediate' | string;
  digest_schedule?: string | null;
  is_default_handler: boolean;
  status: 'active' | 'disabled' | string;
  filters?: Record<string, unknown> | null;
  settings?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface EmailAccount {
  public_id: string;
  provider: EmailProvider | string;
  email_address: string;
  display_name?: string | null;
  status: 'active' | 'disabled' | 'disabled_by_package' | 'revoked' | 'error' | string;
  sync_mode?: string | null;
  source_public_id?: string | null;
  source_status?: string | null;
  sync_cursor?: string | null;
  history_id?: string | null;
  watch_expiration?: string | null;
  last_synced_at?: string | null;
  last_error?: string | null;
  settings?: Record<string, unknown> | null;
  retention?: {
    raw_days?: number;
    content_days?: number;
    vector_days?: number;
  };
  reconciliation?: {
    stale_after_minutes?: number;
  };
  bindings?: EmailAgentBinding[];
  created_at: string;
  updated_at: string;
}

export interface EmailBlacklistRule {
  public_id: string;
  email_account_id?: number | null;
  pattern_type: 'exact_sender' | 'domain' | 'regex' | string;
  pattern_value: string;
  normalized_value: string;
  action: 'skip_only' | 'auto_delete' | string;
  enabled: boolean;
  last_matched_count?: number;
  created_at: string;
  updated_at: string;
}

export interface EmailMessage {
  public_id: string;
  provider_message_id: string;
  provider_thread_id?: string | null;
  from_address: string;
  from_domain: string;
  subject?: string | null;
  snippet?: string | null;
  status: string;
  received_at?: string | null;
  created_at: string;
  updated_at: string;
  error_message?: string | null;
}

export interface EmailSummary {
  public_id: string;
  summary_type: string;
  trigger_type: string;
  status: string;
  title?: string | null;
  question?: string | null;
  summary?: string | null;
  item_count: number;
  model_used?: string | null;
  generated_at?: string | null;
  created_at: string;
}

export interface EmailDraftApproval {
  public_id: string;
  action_type: string;
  status: string;
  title?: string | null;
  draft_text?: string | null;
  risk_level: string;
  approved_at?: string | null;
  rejected_at?: string | null;
  executed_at?: string | null;
  error_message?: string | null;
  message?: {
    public_id?: string;
    from_address?: string;
    subject?: string | null;
    received_at?: string | null;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface ChatDiagram {
  id: string;
  title?: string;
  diagramType: string;
  mermaid: string;
  description?: string;
}

export interface ChatActionConfirmation {
  id: string;
  toolPublicId?: string;
  toolName: string;
  actionTitle?: string;
  summary?: string;
  riskLevel: 'read' | 'write' | 'sensitive';
  endpointUrl?: string;
  parameters: Record<string, unknown>;
  idempotencyKey?: string;
  status: 'pending' | 'executing' | 'confirmed' | 'cancelled' | 'failed';
  resultMessage?: string;
  messageId?: string;
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  role: 'user' | 'agent';
  content: string;
  reasoning?: string | null;
  sources?: ChatSource[];
  attachments?: ChatMessageAttachment[];
  diagrams?: ChatDiagram[];
  confirmation?: ChatActionConfirmation;
  conversationTitle?: string;
  anonymousLimit?: AnonymousLimitError;
  /** A text_to_image turn is running; the bubble shows an image skeleton until the file arrives. */
  pendingImage?: boolean;
  timestamp: Date;
  status?: 'sending' | 'sent' | 'failed';
}

export interface ChatMessageAttachment {
  publicId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  fileUrl?: string | null;
}

export interface ChatSource {
  marker: string;
  url?: string;
  title: string;
  /** Uploaded-knowledge sources: heading path of the cited chunk. */
  section?: string;
  /** Uploaded-knowledge sources: leading text of the cited chunk. */
  excerpt?: string;
}

export interface SharedConversationMessage {
  publicId: string;
  role: 'user' | 'agent';
  content: string;
  type?: string;
  createdAt: string;
  sources?: ChatSource[];
  attachments?: Array<ChatMessageAttachment & { displayMode?: 'PREVIEW' | 'DOWNLOAD' | 'OMITTED' }>;
}

export interface SharedConversation {
  publicId: string;
  title: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string | null;
  messageCount: number;
  messages: SharedConversationMessage[];
}

export interface ChatShare {
  publicId: string;
  shareToken: string;
  sharePath: string;
  shareUrl?: string;
  title?: string | null;
  status: string;
  snapshotVersion: number;
  messageCount: number;
  expiresAt?: string | null;
  revokedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SendMessageRequest {
  sessionId: string;
  content: string;
}

export interface SendMessageResponse {
  message: ChatMessage;
  agentResponse?: ChatMessage;
}

// ============================================
// Anonymous User (NEW)
// ============================================

export interface AnonymousUserStats {
  sessionsUsed: number;
  maxSessions: number;
  totalMessages: number;
  totalTokens: number;
  createdAt: string;
  sessions: {
    id: number;
    publicId: string;
    messageCount: number;
    tokenCount: number;
    createdAt: string;
  }[];
}

// ============================================
// Enhanced Package (Token-based)
// ============================================

export interface EnhancedPackage {
  publicId: string;
  code: string;
  name: string;
  price: number;
  duration: number; // days

  maxAgents: number;
  capabilities?: string[];

  // Trial-specific
  isTrial: boolean;
  trialDurationDays?: number;

  isActive: boolean;
  createdAt?: string;
}

// ============================================
// Enhanced Subscription (NEW)
// ============================================

export interface CurrentSubscriptionDetails {
  publicId: string;
  userId: number;
  packageId: number;
  startAt: string;
  endAt: string;
  status: 'trialing' | 'active' | 'expired' | 'canceled';

  // Trial info
  isTrialSubscription: boolean;
  trialEndsAt: string | null;
  willDowngradeTo: number | null;

  package: EnhancedPackage;
}

// ============================================
// API Error Responses (NEW)
// ============================================

export interface QuotaExceededError {
  error?: 'Quota exceeded' | 'QUOTA_EXCEEDED' | 'TOKEN_QUOTA_EXCEEDED';
  errorCode?: 'QUOTA_EXCEEDED' | 'TOKEN_QUOTA_EXCEEDED';
  message: string;
  upgradeUrl?: string;
  packageStatus?: 'available' | 'low' | 'empty';
  hasAdvanceBalance?: boolean;
  chatFallbackEnabled?: boolean;
  actions?: string[];
}

export interface RateLimitError {
  error?: 'Rate limit exceeded' | 'RATE_LIMIT_EXCEEDED';
  errorCode?: 'RATE_LIMIT_EXCEEDED';
  message: string;
  retryAfter: number; // seconds
}

export interface AnonymousLimitError {
  error?:
    | 'ANONYMOUS_LIMIT_EXCEEDED'
    | 'Anonymous session limit exceeded'
    | 'Anonymous message limit exceeded';
  errorCode?: 'ANONYMOUS_LIMIT_EXCEEDED';
  message: string;
  limitType?: string;
  limit?: number;
  used?: number;
  resetAt?: string;
  sessionsUsed?: number;
  maxSessions?: number;
  upgradeUrl: string;
}

// ============================================
// Custom Tools & External Webhooks
// ============================================

export type ToolRiskLevel = 'read' | 'write' | 'sensitive';
export type ToolHitlMode = 'not_required' | 'required' | 'owner_bypass_allowed';

export interface ToolResponsePolicy {
  modelInputMode?: 'raw_json' | string;
  allowedJsonPaths?: string[];
  defaultResultPath?: string;
  redactPaths?: string[];
  maxItems?: number;
  maxResponseBytes?: number;
}

export interface ToolDefinition {
  id?: number;
  publicId: string;
  name: string;
  description?: string | null;
  clientId?: string | null;
  agentId?: number | null;
  agentPublicId?: string | null;
  endpointUrl: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | string;
  authType: 'none' | 'bearer_token' | 'api_key_header' | 'api_key_query' | 'hmac_sha256' | string;
  authSecretRef?: string | null;
  authSecretId?: number | null;
  authHeaderName?: string | null;
  authQueryParamName?: string | null;
  parametersSchema: Record<string, unknown>;
  outputSchema?: Record<string, unknown> | null;
  responsePolicy?: ToolResponsePolicy | Record<string, unknown> | null;
  riskLevel: ToolRiskLevel;
  hitlMode: ToolHitlMode;
  requiresConfirmation?: boolean;
  timeoutMs: number;
  retryPolicy?: Record<string, unknown> | null;
  rateLimitPolicy?: Record<string, unknown> | null;
  status: 'active' | 'disabled';
  metadata?: Record<string, unknown> | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface CreateToolDefinitionInput {
  name: string;
  description?: string;
  client_id?: string;
  agent_public_id?: string;
  endpoint_url: string;
  method?: string;
  auth_type?: string;
  auth_secret_id?: number | null;
  auth_header_name?: string | null;
  auth_query_param_name?: string | null;
  parameters_schema: Record<string, unknown>;
  output_schema?: Record<string, unknown> | null;
  response_policy?: ToolResponsePolicy | Record<string, unknown> | null;
  risk_level?: ToolRiskLevel;
  hitl_mode?: ToolHitlMode;
  requires_confirmation?: boolean;
  timeout_ms?: number;
  status?: 'active' | 'disabled';
}

export interface UserSecret {
  id: number;
  publicId: string;
  name: string;
  description?: string | null;
  keyVersion?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ToolPolicies {
  allowDeleteMethod: boolean;
}

export interface ToolTestCallInput {
  endpoint_url: string;
  method?: string;
  auth_type?: string;
  auth_secret_id?: number | null;
  auth_header_name?: string | null;
  auth_query_param_name?: string | null;
  raw_secret_value?: string;
  parameters?: Record<string, unknown>;
  response_policy?: ToolResponsePolicy | Record<string, unknown>;
}

export interface ToolTestCallResult {
  ok: boolean;
  status: number;
  latencyMs: number;
  rawResponse: unknown;
  modelPreview: unknown;
}

export interface ToolConfirmInput {
  action_id?: string;
  message_id?: string;
  session_id?: string;
  tool_public_id?: string;
  tool_name?: string;
  decision: 'confirm' | 'cancel';
  idempotency_key?: string;
  parameters?: Record<string, unknown>;
  endpoint_url?: string;
}

export interface ToolConfirmResult {
  ok: boolean;
  status: 'confirmed' | 'cancelled';
  actionId?: string;
  toolName?: string;
  httpStatus?: number;
  latencyMs?: number;
  data?: unknown;
  message?: string;
}
